import { useState } from "react";
import { useSessionTenantOrigin } from "../../../hooks/useSessionTenantOrigin";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { Input } from "../../Input";
import { Modal } from "../../Modal";
import { Text } from "../../Text";
import { FieldLabel, ModalContent, ModalFooter } from "../shared";

export type OidcProviderValues = {
  name: string;
  issuer: string;
  audience: string;
  jwksUri?: string;
};

export function OidcProviderModal({
  initial,
  onClose,
  onSubmit,
  alert = false,
}: {
  initial?: OidcProviderValues;
  onClose: () => void;
  onSubmit: (values: OidcProviderValues) => void;
  alert?: boolean;
}) {
  const editing = initial !== undefined;
  const appOrigin = useSessionTenantOrigin();
  const [name, setName] = useState(initial?.name ?? "");
  const [issuer, setIssuer] = useState(initial?.issuer ?? "");
  // Default to this stack's API URL: the audience must match what the workload
  // requests, and the API is the resource being authenticated to.
  const [audience, setAudience] = useState(initial?.audience ?? appOrigin);
  const [jwksUri, setJwksUri] = useState(initial?.jwksUri ?? "");

  const canSubmit =
    name.trim().length > 0 &&
    issuer.trim().length > 0 &&
    audience.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      name: name.trim(),
      issuer: issuer.trim(),
      audience: audience.trim(),
      jwksUri: jwksUri.trim() || undefined,
    });
    onClose();
  };

  return (
    <Modal open onClose={onClose} alert={alert}>
      <ModalContent>
        <Text variant="text-regular-md">
          {editing ? "Edit identity provider" : "Register identity provider"}
        </Text>

        <FieldLabel>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">Name</Text>
          </Box>
          <Input
            autoFocus
            placeholder="e.g. GitHub Actions"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ minWidth: 0, width: "100%" }}
          />
        </FieldLabel>

        <FieldLabel>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">Issuer</Text>
          </Box>
          <Input
            placeholder="https://token.actions.githubusercontent.com"
            value={issuer}
            onChange={(e) => setIssuer(e.target.value)}
            style={{ minWidth: 0, width: "100%" }}
          />
          <Box horizontal={8} top={6}>
            <Text variant="text-small" color="var(--text-secondary)">
              The provider’s `iss` claim. Must be a public HTTPS URL.
            </Text>
          </Box>
        </FieldLabel>

        <FieldLabel>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">Audience</Text>
          </Box>
          <Input
            placeholder="https://your-3b.example.com"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            style={{ minWidth: 0, width: "100%" }}
          />
          <Box horizontal={8} top={6}>
            <Text variant="text-small" color="var(--text-secondary)">
              The `aud` claim tokens must carry. Defaults to this app’s API URL
              — the workload must request the same value.
            </Text>
          </Box>
        </FieldLabel>

        <FieldLabel>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">JWKS URL (optional)</Text>
          </Box>
          <Input
            placeholder="Defaults to {issuer}/.well-known/jwks.json"
            value={jwksUri}
            onChange={(e) => setJwksUri(e.target.value)}
            style={{ minWidth: 0, width: "100%" }}
          />
        </FieldLabel>

        <div style={{ height: 8 }} />
        <ModalFooter>
          <Button size="l" variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button size="l" onClick={handleSubmit} disabled={!canSubmit}>
            {editing ? "Save" : "Register"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
